// src/components/TagInput.jsx
import React, { useState, useEffect, useRef } from 'react';

const TagInput = ({ initialTags = '', onChange, existingTags = [] }) => {
  // Convert comma-separated string to array
  const parseTagString = (tagString) => {
    return tagString
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag !== '');
  };

  // Convert array back to comma-separated string
  const stringifyTags = (tagArray) => {
    return tagArray.join(',');
  };

  const [tags, setTags] = useState(parseTagString(initialTags));
  const [inputValue, setInputValue] = useState('');
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef(null);
  const suggestionRef = useRef(null);

  // Update parent component when tags change - fixed to prevent infinite rerenders
  useEffect(() => {
    if (onChange) {
      onChange(stringifyTags(tags));
    }
  }, [tags]); // Removed onChange from dependency array to prevent infinite loop

  // Update local state when initialTags prop changes
  useEffect(() => {
    setTags(parseTagString(initialTags));
  }, [initialTags]);

  // Handle outside clicks to close suggestion box
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (
        suggestionRef.current && 
        !suggestionRef.current.contains(e.target) && 
        !inputRef.current.contains(e.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);

    // Show suggestions when input has content
    if (value.trim()) {
      // Filter existing tags that match input value and aren't already selected
      // Convert to lowercase for consistent filtering
      const lowerValue = value.toLowerCase();
      const filtered = existingTags.filter(tag => 
        tag.toLowerCase().includes(lowerValue) && 
        !tags.includes(tag.toLowerCase())
      );
      setFilteredSuggestions(filtered);
      setShowSuggestions(true);
    } else {
      setFilteredSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleKeyDown = (e) => {
    // Add tag on Enter or comma
    if ((e.key === 'Enter' || e.key === ',') && inputValue.trim()) {
      e.preventDefault();
      addTag(inputValue);
    } 
    // Remove last tag on Backspace when input is empty
    else if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
      const newTags = [...tags];
      newTags.pop();
      setTags(newTags);
    }
  };

  const addTag = (tag) => {
    // Clean the tag (remove commas, trim, convert to lowercase, and replace spaces with underscores)
    const cleanedTag = tag.replace(/,/g, '').trim().toLowerCase().replace(/\s+/g, '_');
    
    // Don't add empty tags or duplicates
    if (cleanedTag && !tags.includes(cleanedTag)) {
      const newTags = [...tags, cleanedTag];
      setTags(newTags);
      setInputValue('');
      setShowSuggestions(false);
    } else {
      setInputValue('');
    }
  };

  const removeTag = (indexToRemove) => {
    setTags(tags.filter((_, index) => index !== indexToRemove));
  };

  const selectSuggestion = (suggestion) => {
    // Convert to lowercase and replace spaces with underscores
    const processedSuggestion = suggestion.toLowerCase().replace(/\s+/g, '_');
    if (!tags.includes(processedSuggestion)) {
      setTags([...tags, processedSuggestion]);
    }
    setInputValue('');
    setShowSuggestions(false);
    // Focus back on input after selection
    inputRef.current.focus();
  };

  return (
    <div className="relative">
      <div 
        className="flex flex-wrap items-center p-2 border rounded min-h-[45px] focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500" 
        onClick={() => inputRef.current.focus()}
      >
        <div className="flex flex-wrap w-full">
          {/* Render selected tags as pills */}
          {tags.map((tag, index) => (
            <div 
              key={index} 
              className="bg-gray-100 border rounded-full flex items-center px-2 py-1 mr-2 mb-1"
            >
              <span className="mr-1">{tag}</span>
              <button 
                type="button"
                className="text-gray-500 hover:text-gray-700" 
                onClick={(e) => {
                  e.stopPropagation(); // Prevent triggering parent onClick
                  removeTag(index);
                }}
                aria-label={`Remove ${tag} tag`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
          
          {/* Input for new tags - fixed height to prevent jumping */}
          <input
            ref={inputRef}
            type="text"
            className="flex-grow outline-none p-0 m-0 min-w-[180px] h-[28px]"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={tags.length ? "" : "Type tags and press Enter (e.g., inspiration, motivation)"}
          />
        </div>
      </div>
      
      {/* Absolute position for suggestions to prevent jitter */}
      <div className="relative">
        {/* Suggestions dropdown */}
        {showSuggestions && filteredSuggestions.length > 0 && (
          <div 
            ref={suggestionRef}
            className="absolute z-10 bg-white border rounded shadow-sm mt-1 w-full max-h-[200px] overflow-y-auto"
          >
            <ul className="divide-y divide-gray-200">
              {filteredSuggestions.map((suggestion, index) => (
                <li 
                  key={index} 
                  className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                  onClick={() => selectSuggestion(suggestion)}
                >
                  {suggestion}
                </li>
              ))}
            </ul>
          </div>
        )}
        
        {/* Add new tag button (shows when input has content but no matching suggestions) */}
        {showSuggestions && inputValue.trim() && filteredSuggestions.length === 0 && (
          <div 
            ref={suggestionRef}
            className="absolute z-10 bg-white border rounded shadow-sm mt-1 w-full"
          >
            <button
              type="button"
              className="w-full px-3 py-2 text-left text-blue-600 hover:bg-gray-100 flex items-center"
              onClick={() => addTag(inputValue)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Create new tag "{inputValue}"
            </button>
          </div>
        )}
      </div>
      
      {/* Hidden input to store actual tag value for form submission */}
      <input 
        type="hidden" 
        name="tags" 
        value={stringifyTags(tags)} 
      />
    </div>
  );
};

export default TagInput;